/**
 * The admin menu (C4 §4): Mongolian, grouped by daily use. Pure so the
 * groups and the route → highlighted item rule are unit-tested.
 */
export type MenuItem = { key: string; label: string; to: string };
export type MenuGroup = { label: string; items: MenuItem[] };

const DAILY: MenuGroup = {
  label: "Өдөр тутам",
  items: [
    { key: "frontDesk", label: "Ресепшн", to: "/front-desk" },
    { key: "bookings", label: "Захиалга", to: "/bookings" },
  ],
};

const SETUP: MenuGroup = {
  label: "Тохиргоо",
  items: [
    { key: "service", label: "Үйлчилгээ", to: "/service" },
    { key: "addOns", label: "Нэмэлт үйлчилгээ", to: "/add-ons" },
    { key: "staff", label: "Ажилтан", to: "/staff" },
    { key: "team", label: "Өрөө ба тоног төхөөрөмж", to: "/team" },
    { key: "branch", label: "Салбар", to: "/branch" },
    { key: "kiosk", label: "Киоск", to: "/kiosk-settings" },
    { key: "hours", label: "Ажлын цаг", to: "/hours" },
    { key: "limits", label: "Хуваалцсан хязгаар", to: "/limits" },
    { key: "category", label: "Ангилал", to: "/category" },
  ],
};

const BUSINESS: MenuGroup = {
  label: "Бизнес",
  items: [
    { key: "report", label: "Тайлан", to: "/" },
    { key: "settings", label: "Бизнесийн тохиргоо", to: "/business/update" },
    { key: "members", label: "Гишүүд", to: "/business/members" },
    { key: "switch", label: "Бизнес солих", to: "/business" },
  ],
};

const PLATFORM: MenuGroup = {
  label: "Платформ",
  items: [
    { key: "allBusinesses", label: "Бүх бизнес", to: "/admin/businesses" },
    { key: "roles", label: "Бизнесийн эрх", to: "/admin/business-roles" },
    { key: "types", label: "Бизнесийн төрөл", to: "/admin/business-types" },
    { key: "users", label: "Хэрэглэгчид", to: "/users" },
    { key: "scheduleAudit", label: "Хуваарийн аудит", to: "/admin/audit" },
    { key: "serviceAudit", label: "Үйлчилгээний аудит", to: "/admin/audit/services" },
  ],
};

/** Platform look setting; listed only when the backend has it (an older one 404s). */
const LOOK_ITEM: MenuItem = { key: "look", label: "Админы загвар", to: "/admin/look" };

export function menuGroups(isPlatformAdmin: boolean, opts: { lookSetting?: boolean } = {}): MenuGroup[] {
  if (!isPlatformAdmin) return [DAILY, SETUP, BUSINESS];
  const platform = opts.lookSetting ? { ...PLATFORM, items: [...PLATFORM.items, LOOK_ITEM] } : PLATFORM;
  return [DAILY, SETUP, BUSINESS, platform];
}

/** Pages off the menu that belong to an item. */
const EXTRA: ReadonlyArray<readonly [string, string]> = [
  ["/offerings", "service"],
  ["/cohort", "service"],
  ["/space", "team"],
  ["/resource", "team"],
  ["/calendar", "hours"],
  ["/admin/users", "users"],
];

/** The menu item a route belongs to (longest prefix); null for pages outside the menu. */
export function selectedKey(path: string): string | null {
  const prefixes: Array<readonly [string, string]> = [
    ...[DAILY, SETUP, BUSINESS, PLATFORM].flatMap((g) => g.items.map((i) => [i.to, i.key] as const)),
    [LOOK_ITEM.to, LOOK_ITEM.key] as const,
    ...EXTRA,
  ];
  let best: readonly [string, string] | null = null;
  for (const p of prefixes) {
    const hit = p[0] === "/" ? path === "/" : path === p[0] || path.startsWith(p[0] + "/");
    if (hit && (!best || p[0].length > best[0].length)) best = p;
  }
  return best ? best[1] : null;
}
